import type { MfaMethod } from "@purlink/shared/auth";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import {
  BackupCodesPanel,
  EmailSetup,
  MethodChoice,
  TotpSetup,
} from "~/components/account/mfa-setup";
import { ChangePasswordForm } from "~/components/account/change-password-form";
import { AuthLayout } from "~/components/auth/auth-layout";
import { Button } from "~/components/ui/button";
import { refreshMe, signOut, useCurrentUser } from "~/lib/auth";
import { cn } from "~/lib/utils";

export const Route = createFileRoute("/_onboarding/onboarding/security")({
  component: SecurityOnboarding,
});

type Step = "password" | "choose" | "setup" | "backup";

function StepDots({ steps, current }: { steps: Step[]; current: Step }) {
  const index = steps.indexOf(current);
  return (
    <ol aria-label="Progress" className="flex items-center gap-1.5">
      {steps.map((step, i) => (
        <li
          key={step}
          aria-current={i === index ? "step" : undefined}
          className={cn(
            "h-1.5 rounded-full transition-all duration-(--duration-base)",
            i === index
              ? "w-6 bg-primary"
              : i < index
                ? "w-1.5 bg-primary"
                : "w-1.5 bg-border-strong",
          )}
        >
          <span className="sr-only">
            Step {i + 1} of {steps.length}
          </span>
        </li>
      ))}
    </ol>
  );
}

function SecurityOnboarding() {
  const { user } = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const needsPassword = user.mustChangePassword;
  const needsMfa = !user.twoFactorEnabled || !user.mfaMethod;

  const steps: Step[] = [
    ...(needsPassword ? (["password"] as const) : []),
    "choose",
    "setup",
    "backup",
  ];
  const [step, setStep] = useState<Step>(needsPassword ? "password" : "choose");
  const [method, setMethod] = useState<MfaMethod | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  const finish = async () => {
    const me = await refreshMe(queryClient);
    await navigate({ to: me?.onboarded ? "/dashboard" : "/onboarding/security" });
  };

  const titles: Record<Step, string> = {
    password: "Choose your own password",
    choose: "Set up two-step sign-in",
    setup: method === "email" ? "Turn on email codes" : "Connect your authenticator app",
    backup: "Save your backup codes",
  };
  const descriptions: Record<Step, string> = {
    password: "You signed in with a temporary password. Replace it with one only you know.",
    choose:
      "Purlink requires a second step at sign-in to keep lead information safe. Choose how you'd like to confirm it's you.",
    setup: "",
    backup: "Two-step sign-in is on.",
  };

  return (
    <AuthLayout
      wide
      title={titles[step]}
      description={descriptions[step] || undefined}
      footer={
        <Button variant="link" size="sm" onClick={() => signOut(queryClient)}>
          Sign out
        </Button>
      }
    >
      <StepDots steps={steps} current={step} />

      {step === "password" && (
        <ChangePasswordForm
          submitLabel="Save password"
          onChanged={async () => {
            await refreshMe(queryClient);
            if (needsMfa) setStep("choose");
            else await finish();
          }}
        />
      )}

      {step === "choose" && (
        <div className="flex flex-col gap-5">
          <MethodChoice value={method} onChange={setMethod} />
          <Button size="lg" disabled={!method} onClick={() => setStep("setup")}>
            Continue
          </Button>
        </div>
      )}

      {step === "setup" && method && (
        <div className="flex flex-col gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="self-start"
            onClick={() => setStep("choose")}
          >
            <ArrowLeft /> Choose a different method
          </Button>
          {method === "totp" ? (
            <TotpSetup
              onComplete={(codes) => {
                setBackupCodes(codes);
                setStep("backup");
              }}
            />
          ) : (
            <EmailSetup
              email={user.email}
              onComplete={(codes) => {
                setBackupCodes(codes);
                setStep("backup");
              }}
            />
          )}
        </div>
      )}

      {step === "backup" && (
        <BackupCodesPanel codes={backupCodes} onDone={finish} doneLabel="Go to Purlink" />
      )}
    </AuthLayout>
  );
}
